/**
 * Rich text for the lesson fields that are NOT highlightable.
 *
 * A field arrives as a plain string in `<field>` beside an optional rich array in
 * `<field>Inline`. The rich key is preferred and the string is the mirror, which
 * is what a build that predates the rich key keeps rendering — so the mirror is
 * never markdown, and a missing rich key is not an error.
 *
 * Highlightable copy does NOT come through here. A verse body or a heading is
 * tokenised into words that carry page indices, so its marks ride on the word
 * objects and are painted by WordRun. This module is for the rest: card copy,
 * captions, accordion titles, story text — text with no index of its own.
 */

/**
 * Marks to Tailwind utilities.
 *
 * NOT marksClass() from lesson-content-words: that returns `.mark-*`, whose rules
 * live in WordRun's SCOPED style block and therefore reach only the spans WordRun
 * itself renders. Used anywhere else they would apply nothing at all, silently.
 */
export function markUtilities(marks) {
  const list = Array.isArray(marks) ? marks : [];
  const classes = [];
  if (list.includes('bold')) classes.push('font-bold');
  if (list.includes('italic')) classes.push('italic');

  // `underline` and `line-through` are both text-decoration-line, so applying the
  // two utilities together would let stylesheet order pick a winner. The pair is
  // one declaration instead.
  const underline = list.includes('underline');
  const strike = list.includes('strike') || list.includes('strikethrough');
  if (underline && strike) classes.push('[text-decoration-line:underline_line-through]');
  else if (underline) classes.push('underline');
  else if (strike) classes.push('line-through');
  if (underline) classes.push('underline-offset-2');

  return classes.join(' ');
}

const isList = seg => seg?.type === 'list';

/**
 * The runs to render for one field.
 *
 * `lists: false` is the default and means this field is a single line — a
 * heading, a caption, a button label. Such a field takes marks but not bulleted
 * lines, both because a list inside an <h4> or a <figcaption> is not something an
 * author can have meant and because `<ul>` is not valid inside the phrasing
 * content those elements hold.
 *
 * When a list arrives in a field that does not take one, the whole rich body is
 * dropped in favour of the plain mirror. The mirror carries the list's own text
 * (extractInlineText joins its lines), so the words still reach the reader — they
 * simply lose their bullets. That is the one behaviour worth having here: the
 * alternative is either invalid markup or copy that silently disappears.
 */
export function fieldRuns(inline, plain, { lists = false } = {}) {
  const rich = Array.isArray(inline) ? inline : [];
  const usable = rich.length > 0 && (lists || !rich.some(isList));
  if (usable) return rich;
  return plain ? [{ type: 'text', text: plain, marks: [] }] : [];
}
